import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Scope values:
 *   null          — parent workspace only (default)
 *   "all"         — whole koncern combined
 *   <orgnr>       — a specific subsidiary
 *
 * The scope is kept in the URL as `?scope=...` so browser back/forward works
 * and deep-links are shareable. Every broker module (Valuation, DDQ, IM,
 * Dashboard, Logg, Dataroom) calls this hook and routes its API fetches
 * accordingly.
 */
export default function useKoncernScope(parentOrgnr) {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawScope = searchParams.get("scope") || "";
  const scope = useMemo(() => {
    if (!rawScope) return null;          // parent-only
    if (rawScope === "all") return "all"; // whole group
    return rawScope;                      // subsidiary orgnr
  }, [rawScope]);

  const setScope = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next === null || next === "" || next === parentOrgnr) {
        params.delete("scope");
      } else {
        params.set("scope", next);
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams, parentOrgnr],
  );

  // The orgnr the current scope should hit for single-org endpoints.
  // For "all" mode the caller should use a combined endpoint instead.
  const activeOrgnr = useMemo(() => {
    if (scope === null) return parentOrgnr;
    if (scope === "all") return null;
    return scope;
  }, [scope, parentOrgnr]);

  return { scope, setScope, activeOrgnr, isAllMode: scope === "all" };
}
